import { Box, Button, CircularProgress, Dialog, Typography, useTheme, Chip, Card, CardContent, Tooltip, IconButton, Slider } from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { makeGetRequest, makePostRequest } from 'utils/api'
import { Radar } from 'react-chartjs-2'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LockIcon from '@mui/icons-material/Lock'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import EditIcon from '@mui/icons-material/Edit'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import AddIcon from '@mui/icons-material/Add'
import PeriodSelectorDialog from './PeriodSelectorDialog'
import AddIdeaPopup from '../../containers/Roadmap/workflows/AddIdeaPopup'
import { roadmapAPI } from '../../containers/Roadmap/api/roadmapAPI'

import {
  Chart as ChartJS,
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js'

ChartJS.register(
  RadialLinearScale,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Title,
  ChartTooltip,
  Legend,
)

// Human-readable French labels for each domain key
// According to LAP-406 specification section 2.2.4.1
const DOMAIN_LABELS = {
  Finance: 'Finance',
  Temps: 'Temps',
  Earth: 'Planète',        // Earth domain displays as "Planète"
  Social: 'Social',
  Professionnel: 'Professionnel',
  Personnel: 'Personnel',  // includes spiritual practices
  Domicile: 'Domicile',    // Home/Family domain
  Administratif: 'Administratif',
  // Legacy mappings for backward compatibility
  Famille: 'Foyer',
  Santé: 'Santé',
}

// Snapshot source type labels
const SNAPSHOT_SOURCE_LABELS = {
  AUTO: 'Automatique',
  MANUAL: 'Manuel',
  ONBOARDING: 'Onboarding',
}

// Snapshot source type colors
const SNAPSHOT_SOURCE_COLORS = {
  AUTO: '#0089ff',
  MANUAL: '#fbca6d',
  ONBOARDING: '#4caf50',
}

// Plan requirement labels
const PLAN_LABELS = {
  FREE: 'Gratuit',
  STANDARD: 'Standard',
  PREMIUM: 'Premium',
}

// Plan requirement colors
const PLAN_COLORS = {
  FREE: '#4caf50',
  STANDARD: '#ff9800',
  PREMIUM: '#9c27b0',
}

// Color palette for multiple series
const SERIES_COLORS = [
  { bg: 'rgba(0, 137, 255, 0.20)', border: '#0089ff', point: '#0089ff' },
  { bg: 'rgba(251, 202, 109, 0.20)', border: '#fbca6d', point: '#fbca6d' },
  { bg: 'rgba(76, 175, 80, 0.20)', border: '#4caf50', point: '#4caf50' },
  { bg: 'rgba(156, 39, 176, 0.20)', border: '#9c27b0', point: '#9c27b0' },
]

export default function GeneralReportEnhanced() {
  const { t, i18n } = useTranslation('layout') // LAP-733 (GE022)
  const navigate = useNavigate()
  const siteUser = useSelector((store) => store.user)
  const theme = useTheme()

  // LAP-733 (GE022) — i18n-aware lookup tables: re-evaluate on each render so
  // they pick up the current language from the i18n hook. Keys must match the
  // backend snapshot_type strings (AUTO/MANUAL/ONBOARDING) and plan tier names.
  const I18N_SNAPSHOT_SOURCE_LABELS = {
    AUTO: t('lifeReport.snapAuto'),
    MANUAL: t('lifeReport.snapManual'),
    ONBOARDING: t('lifeReport.snapOnboarding'),
  }
  const I18N_PLAN_LABELS = {
    FREE: t('lifeReport.planFree'),
    STANDARD: t('lifeReport.planStandard'),
    PREMIUM: t('lifeReport.planPremium'),
  }

  const [loading, setLoading] = useState(true)
  const [radarData, setRadarData] = useState(null)
  const [latestSnapshot, setLatestSnapshot] = useState(null)
  const [inspirationalQuote, setInspirationalQuote] = useState(null)
  const [domainTexts, setDomainTexts] = useState({})
  const [error, setError] = useState(null)
  const [regenerating, setRegenerating] = useState(false)
  const [availablePeriods, setAvailablePeriods] = useState([])
  const [selectedPeriod, setSelectedPeriod] = useState(null)
  const [showInfoDialog, setShowInfoDialog] = useState(false)
  const [showManualDialog, setShowManualDialog] = useState(false)
  const [showPeriodDialog, setShowPeriodDialog] = useState(false)
  const [showDomainHelpDialog, setShowDomainHelpDialog] = useState(false)
  const [manualScores, setManualScores] = useState({})
  const [allDomains, setAllDomains] = useState([])
  const [showAddIdeaDialog, setShowAddIdeaDialog] = useState(false)

  const fetchDomains = async () => {
    try {
      const response = await makeGetRequest('/api/general/domains/', {}, true)
      if (response?.status === 200 && response.data) {
        // Filter to only Life Resume domains (LAP-406 specification section 2.2.4.1)
        const lifeResumeDomains = ['Finance', 'Temps', 'Earth', 'Social', 'Professionnel', 'Personnel', 'Domicile', 'Administratif']
        const filteredDomains = response.data.filter(domain => 
          lifeResumeDomains.includes(domain.domain)
        )
        setAllDomains(filteredDomains)
      }
    } catch (err) {
      console.error('Failed to fetch domains:', err)
    }
  }

  const fetchAvailablePeriods = async () => {
    try {
      const response = await makeGetRequest('/api/general/life-resume/periods/', {}, true)
      const periods = (response?.status === 200 && response.data?.periods) || []
      setAvailablePeriods(periods)
      if (periods.length > 0) {
        setSelectedPeriod(periods[0])
        return
      }
    } catch (err) {
      console.error('Failed to fetch available periods:', err)
    }
    // Fallback: no period-bound snapshots yet (e.g. brand-new user with only
    // an ONBOARDING baseline). Seed a current-month period so fetchAllData
    // can still run and render the radar.
    const now = new Date()
    setSelectedPeriod({
      period_type: 'MONTH',
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      label: now.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }),
    })
  }

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      // Build query parameters for API calls
      let radarUrl = '/api/general/life-resume/radar/'
      let latestUrl = '/api/general/life-resume/latest/'
      
      if (selectedPeriod) {
        const params = new URLSearchParams()
        params.append('period_type', selectedPeriod.period_type)
        params.append('year', selectedPeriod.year)
        if (selectedPeriod.month) {
          params.append('month', selectedPeriod.month)
        }
        
        const queryString = params.toString()
        radarUrl += '?' + queryString
        latestUrl += '?' + queryString
        
        console.log('Fetching data for period:', selectedPeriod)
        console.log('Radar URL:', radarUrl)
        console.log('Latest URL:', latestUrl)
      }

      // Fetch all data in parallel
      const [radarRes, latestRes, quotesRes, textsRes] = await Promise.all([
        makeGetRequest(radarUrl, {}, true),
        makeGetRequest(latestUrl, {}, true),
        makeGetRequest('/api/general/life-resume/quotes/', {}, true),
        makeGetRequest('/api/general/life-resume/domain-texts/', {}, true),
      ])

      const hasRadarSeries = radarRes?.status === 200
        && radarRes.data
        && Array.isArray(radarRes.data.series)
        && radarRes.data.series.length > 0

      if (radarRes?.status === 200 && radarRes.data) {
        setRadarData(radarRes.data)
      } else {
        setRadarData(null)
      }
      let effectiveLatest = null
      if (latestRes?.status === 200 && latestRes.data) {
        effectiveLatest = latestRes.data
      } else if (hasRadarSeries) {
        // No period-scoped snapshot for the selected period, but the radar has
        // something (typically the ONBOARDING baseline). Fall back to the most
        // recent ONBOARDING snapshot so the cards can render.
        try {
          const fallbackRes = await makeGetRequest(
            '/api/general/life-resume/latest/?type=ONBOARDING',
            {},
            true,
          )
          if (fallbackRes?.status === 200 && fallbackRes.data) {
            effectiveLatest = fallbackRes.data
          }
        } catch (fallbackErr) {
          console.warn('ONBOARDING latest fallback failed:', fallbackErr)
        }
      }

      if (effectiveLatest) {
        // Calculate global score if missing
        if (effectiveLatest.domain_scores && !effectiveLatest.global_score) {
          const eligibleScores = effectiveLatest.domain_scores
            .filter(ds => ds.is_eligible && ds.score !== null && ds.score !== undefined)
            .map(ds => parseFloat(ds.score))

          if (eligibleScores.length > 0) {
            const calculatedGlobalScore = eligibleScores.reduce((sum, score) => sum + score, 0) / eligibleScores.length
            effectiveLatest.global_score = calculatedGlobalScore
            effectiveLatest.eligible_domain_count = eligibleScores.length
            effectiveLatest.data_completeness_rate = (eligibleScores.length / effectiveLatest.domain_scores.length) * 100
          }
        }

        // DRF DecimalField serializes to strings — coerce numerics so
        // .toFixed() / numeric comparisons work downstream.
        if (effectiveLatest.global_score !== null && effectiveLatest.global_score !== undefined) {
          effectiveLatest.global_score = Number(effectiveLatest.global_score)
        }
        if (effectiveLatest.data_completeness_rate !== null && effectiveLatest.data_completeness_rate !== undefined) {
          effectiveLatest.data_completeness_rate = Number(effectiveLatest.data_completeness_rate)
        }

        setLatestSnapshot(effectiveLatest)
      } else {
        setLatestSnapshot(null)
      }
      if (quotesRes?.status === 200 && quotesRes.data?.quotes?.length > 0) {
        // Select a random quote
        const quotes = quotesRes.data.quotes
        const randomQuote = quotes[Math.floor(Math.random() * quotes.length)]
        setInspirationalQuote(randomQuote)
      }
      if (textsRes?.status === 200 && textsRes.data?.templates) {
        // Index templates by domain and score bucket
        const textIndex = {}
        textsRes.data.templates.forEach(template => {
          if (!textIndex[template.domain]) {
            textIndex[template.domain] = {}
          }
          textIndex[template.domain][template.score_bucket] = template
        })
        setDomainTexts(textIndex)
      }

      // If radar is empty AND no latest snapshot, show empty state.
      // (An ONBOARDING-only user has radar series → stay on the normal view.)
      if (!hasRadarSeries && latestRes?.status === 404) {
        setError('no_data')
      }
    } catch (err) {
      console.error('Failed to fetch life resume data:', err)
      setError('fetch_error')
    } finally {
      setLoading(false)
    }
  }, [selectedPeriod])

  useEffect(() => {
    const initializeData = async () => {
      await fetchAvailablePeriods()
      await fetchDomains()
    }
    initializeData()
  }, [])

  useEffect(() => {
    if (selectedPeriod) {
      fetchAllData() // Full UI refresh when period changes
    }
  }, [selectedPeriod, fetchAllData])

  const handleRegenerateAuto = async () => {
    setRegenerating(true)
    try {
      let requestData = { force: true }
      
      if (selectedPeriod) {
        // Use the selected period for snapshot generation
        requestData = {
          ...requestData,
          period_type: selectedPeriod.period_type,
          year: selectedPeriod.year
        }
        
        if (selectedPeriod.month) {
          requestData.month = selectedPeriod.month
        }
        
        console.log('Generating AUTO snapshot for selected period:', requestData)
      } else {
        // Fallback to current date if no period selected
        const now = new Date()
        requestData = {
          ...requestData,
          period_type: 'MONTH',
          year: now.getFullYear(),
          month: now.getMonth() + 1
        }
        
        console.log('Generating AUTO snapshot for current period:', requestData)
      }
      
      await makePostRequest('/api/general/life-resume/generate-auto/', requestData, {}, true)
      await fetchAllData()
      await fetchAvailablePeriods() // Refresh available periods
    } catch (err) {
      console.error('Failed to regenerate AUTO snapshot:', err)
    } finally {
      setRegenerating(false)
    }
  }

  const handleCreateManual = async () => {
    setShowManualDialog(true)
    // Initialize manual scores with current domain scores if available
    if (latestSnapshot?.domain_scores) {
      const scores = {}
      latestSnapshot.domain_scores.forEach(ds => {
        const score = Number(ds.score || ds.final_score) || 5
        scores[ds.domain] = score
      })
      setManualScores(scores)
    } else if (allDomains.length > 0) {
      // Initialize with default scores
      const scores = {}
      allDomains.forEach(domain => {
        scores[domain.domain] = 5
      })
      setManualScores(scores)
    }
  }

  const handleSaveManualEvaluation = async () => {
    try {
      let periodStart, periodEnd, periodType, year, month
      
      if (selectedPeriod) {
        // Use the selected period for manual snapshot
        periodType = selectedPeriod.period_type
        year = selectedPeriod.year
        month = selectedPeriod.month
        
        if (periodType === 'YEAR') {
          periodStart = new Date(Date.UTC(year, 0, 1)) // January 1st
          periodEnd = new Date(Date.UTC(year, 11, 31)) // December 31st
        } else {
          // MONTH - Use a more reliable date calculation
          const monthIndex = (month || 1) - 1 // Convert to 0-based index
          
          // Create dates in UTC to avoid timezone issues
          periodStart = new Date(Date.UTC(year, monthIndex, 1))
          periodEnd = new Date(Date.UTC(year, monthIndex + 1, 0))
          
          // Debug: Verify the dates are correct
          console.log('Date calculation debug:', {
            selectedMonth: month,
            monthIndex: monthIndex,
            periodStart: periodStart.toISOString().split('T')[0],
            periodEnd: periodEnd.toISOString().split('T')[0],
            expectedStart: `${year}-${String(month).padStart(2, '0')}-01`,
            expectedEnd: `Should be last day of ${year}-${String(month).padStart(2, '0')}`
          })
        }
        
        console.log('Creating MANUAL snapshot for selected period:', {
          period_type: periodType,
          year: year,
          month: month,
          period_start: periodStart.toISOString().split('T')[0],
          period_end: periodEnd.toISOString().split('T')[0]
        })
      } else {
        // Fallback to current month if no period selected
        const now = new Date()
        year = now.getFullYear()
        month = now.getMonth() + 1
        periodType = 'MONTH'
        
        const monthIndex = now.getMonth()
        periodStart = new Date(Date.UTC(year, monthIndex, 1))
        periodEnd = new Date(Date.UTC(year, monthIndex + 1, 0))
        
        console.log('Creating MANUAL snapshot for current month (fallback)')
      }
      
      const response = await makePostRequest('/api/general/life-resume/create-manual/', {
        period_type: periodType,
        period_start: periodStart.toISOString().split('T')[0],
        period_end: periodEnd.toISOString().split('T')[0],
        domain_scores: manualScores
      }, {}, true)
      
      console.log('Manual snapshot API response:', response)
      
      if (response?.status === 201) {
        console.log('Manual snapshot created successfully')
        setShowManualDialog(false)
        await fetchAllData()
        await fetchAvailablePeriods() // Refresh available periods
      } else {
        console.error('Failed to create manual snapshot. Response:', response)
        // Show error to user
        // LAP-733 (GE022)
        alert(`${t('lifeReport.createError')}: ${response?.data?.message || response?.data?.detail || t('lifeReport.unknownError')}`)
      }
    } catch (err) {
      console.error('Failed to create manual snapshot:', err)
    }
  }

  const handleManualScoreChange = (domain, value) => {
    setManualScores(prev => ({
      ...prev,
      [domain]: Number(value) || 0
    }))
  }

  const handleCreateIdea = () => {
    setShowAddIdeaDialog(true)
  }

  const handleIdeaSubmit = async (ideaData) => {
    try {
      await roadmapAPI.createIdea({
        ...ideaData,
        horizon: 'dreams', // Default to dreams horizon as per LAP-406 spec
        status: 'idea'
      })
      setShowAddIdeaDialog(false)
      // Optional: Show success message
      // LAP-733 (GE022)
      console.log(t('lifeReport.ideaCreated'))
    } catch (err) {
      console.error('Failed to create idea:', err)
    }
  }

  // Format period for display in button. LAP-733 (GE022) — month names now come
  // from Intl.DateTimeFormat keyed off the active i18next language so we cover
  // fr/en/de/it/es without maintaining a hand-rolled table.
  const formatPeriodLabel = (period) => {
    if (!period) return t('lifeReport.selectPeriod')
    if (period.period_type === 'YEAR') {
      return `${period.year}`
    }
    if (period.period_type === 'MONTH' && period.month) {
      const locale = (i18n.language || 'fr').slice(0, 2)
      const monthName = new Intl.DateTimeFormat(locale, { month: 'long' })
        .format(new Date(period.year, period.month - 1, 1))
      const capitalized = monthName.charAt(0).toUpperCase() + monthName.slice(1)
      return `${capitalized} ${period.year}`
    }
    return `${period.year}`
  }

  // Handle period selection from dialog
  const handlePeriodApply = (period) => {
    console.log('Period selected:', period)
    setSelectedPeriod(period)
  }

  // Build chart.js data from API radar response
  const buildChartData = () => {
    if (!radarData?.domains || !radarData?.series?.length) return null

    const labels = radarData.domains.map((d) => DOMAIN_LABELS[d] || d)

    const datasets = radarData.series.map((s, i) => {
      const colors = SERIES_COLORS[i % SERIES_COLORS.length]
      // La « Note du jour » (score calculé automatiquement par l'app,
      // snapshot AUTO/DAY) doit RESSORTIR nettement du fond de référence
      // (onboarding), sinon elle se noie sous le remplissage translucide. On lui
      // donne un tracé doré épais SANS remplissage et des points plus gros ; sans
      // remplissage, la ligne reste bien visible quel que soit l'ordre de tracé.
      const isDaily = s.period_type === 'DAY' || s.snapshot_type === 'AUTO'
      if (isDaily) {
        return {
          label: s.label || I18N_SNAPSHOT_SOURCE_LABELS[s.snapshot_type] || s.snapshot_type,
          data: s.scores.map((v) => (v !== null ? v : 0)),
          fill: false,
          backgroundColor: 'rgba(251, 202, 109, 0.15)',
          borderColor: '#fbca6d',
          pointBackgroundColor: '#fbca6d',
          pointBorderColor: '#0a1929',
          pointRadius: 4,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#fbca6d',
          borderWidth: 3,
        }
      }
      return {
        // LAP-733 (GE022) — prefer the i18n source-label table.
        label: s.label || I18N_SNAPSHOT_SOURCE_LABELS[s.snapshot_type] || s.snapshot_type,
        data: s.scores.map((v) => (v !== null ? v : 0)),
        fill: true,
        backgroundColor: colors.bg,
        borderColor: colors.border,
        pointBackgroundColor: colors.point,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: colors.border,
        borderWidth: 2,
      }
    })

    return { labels, datasets }
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      r: {
        min: 0,
        max: 10,
        ticks: {
          stepSize: 2,
          display: true,
          color: 'rgba(255, 255, 255, 0.5)',
          backdropColor: 'transparent',
          font: { size: 11 },
        },
        grid: {
          color: 'rgba(255, 255, 255, 0.15)',
          lineWidth: 1,
        },
        angleLines: {
          color: 'rgba(255, 255, 255, 0.15)',
        },
        pointLabels: {
          color: '#fbca6d',
          font: { size: 14, weight: '600' },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: 'rgba(255, 255, 255, 0.85)',
          padding: 20,
          font: { size: 13 },
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.r}/10`,
        },
      },
      datalabels: {
        display: false,
      },
    },
  }

  // Get score bucket for a score value
  const getScoreBucket = (score) => {
    if (score === null || score === undefined) return null
    if (score >= 9) return '9_10'
    if (score >= 7) return '7_8'
    if (score >= 6) return '6_7'
    if (score >= 5) return '5_6'
    if (score >= 3) return '3_4'
    return '0_2'
  }

  const chartData = buildChartData()

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.appBar?.main || '#0a1929',
        color: 'white',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ minHeight: '100vh', pb: 6 }}>
        {/* Three-zone Header */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            px: 3, 
            py: 2.5,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(0,0,0,0.2)',
          }}
        >
          {/* LEFT: Screen Identity */}
          <Box display="flex" alignItems="center" gap={1.5} flex={1}>
            <Box
              component="img"
              src="/IconEquilibre.png"
              alt="Équilibre"
              sx={{ 
                width: 28, 
                height: 28,
                filter: 'brightness(0) saturate(100%) invert(77%) sepia(45%) saturate(1234%) hue-rotate(15deg) brightness(103%) contrast(96%)'
              }}
            />
            <Typography variant="h6" fontWeight={600} sx={{ color: 'white' }}>
              {/* LAP-733 (GE022) */}
              {t('lifeReport.overviewTitle')}
            </Typography>
          </Box>

          {/* CENTER: Period Selector */}
          <Box display="flex" justifyContent="center" flex={1}>
            {availablePeriods.length > 0 && (
              <Button
                onClick={() => setShowPeriodDialog(true)}
                startIcon={<CalendarTodayIcon />}
                sx={{
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  border: '1px solid rgba(255,255,255,0.15)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    borderColor: 'rgba(255,255,255,0.25)',
                  },
                }}
              >
                {formatPeriodLabel(selectedPeriod)}
              </Button>
            )}
          </Box>

          {/* RIGHT: Actions & Help */}
          <Box display="flex" alignItems="center" gap={1.5} flex={1} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleCreateManual}
              startIcon={<EditIcon />}
              sx={{
                bgcolor: '#fbca6d',
                color: '#0a1929',
                textTransform: 'none',
                fontWeight: 600,
                px: 2.5,
                '&:hover': { 
                  bgcolor: '#f5c05d',
                },
              }}
            >
              {/* LAP-733 (GE022) */}
              {t('lifeReport.manualEval')}
            </Button>
            <Tooltip title="Aide">
              <IconButton
                onClick={() => setShowInfoDialog(true)}
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.08)',
                    color: 'white',
                  },
                }}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: { xs: 2, md: 4 } }}>
          <Box
            sx={{
              bgcolor: 'rgba(40, 56, 77, 0.6)',
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              backdropFilter: 'blur(10px)',
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <CircularProgress sx={{ color: '#0089ff' }} />
              </Box>
            ) : error === 'no_data' ? (
              /* Empty state */
              <Box textAlign="center" py={8}>
                <Typography variant="h6" mb={2}>
                  {/* LAP-733 (GE022) */}
                  {t('lifeReport.noData')}
                  {selectedPeriod && (
                    <Typography component="span" sx={{ display: 'block', color: 'rgba(255,255,255,0.6)', fontSize: '1rem', fontWeight: 400, mt: 1 }}>
                      pour {formatPeriodLabel(selectedPeriod)}
                    </Typography>
                  )}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.6)', mb: 3, maxWidth: 420, mx: 'auto' }}
                >
                  {/* LAP-733 (GE022) */}
                  {t('lifeReport.emptyHint')}
                </Typography>
                <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                  <Button
                    variant="contained"
                    onClick={handleRegenerateAuto}
                    disabled={regenerating}
                    startIcon={regenerating ? <CircularProgress size={18} /> : <AutoAwesomeIcon />}
                    sx={{
                      bgcolor: '#0089ff',
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#0070cc' },
                    }}
                  >
                    {/* LAP-733 (GE022) */}
                    {regenerating ? t('lifeReport.generating') : t('lifeReport.autoReport')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleCreateManual}
                    startIcon={<EditIcon />}
                    sx={{
                      borderColor: '#fbca6d',
                      color: '#fbca6d',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#fbca6d', bgcolor: 'rgba(251, 202, 109, 0.1)' },
                    }}
                  >
                    Évaluation manuelle
                  </Button>
                </Box>
              </Box>
            ) : error === 'fetch_error' ? (
              <Box textAlign="center" py={8}>
                <Typography variant="body1" color="error">
                  {/* LAP-733 (GE022) */}
                  {t('lifeReport.loadError')}
                </Typography>
                <Button onClick={fetchAllData} sx={{ color: '#0089ff', mt: 2 }}>
                  {t('lifeReport.retry')}
                </Button>
              </Box>
            ) : chartData ? (
              <>
                {/* ÉQUILIBRE GLOBAL Section */}
                <Box mb={5}>
                  <Typography variant="h6" fontWeight={700} mb={3} sx={{ color: 'white' }}>
                    {/* LAP-733 (GE022) */}
                    {t('lifeReport.globalBalanceCaps')}
                  </Typography>
                  
                  {/* Dynamic inspirational quote */}
                  {inspirationalQuote && (
                    <Box display="flex" alignItems="flex-start" gap={1} mb={4}>
                      <FormatQuoteIcon sx={{ color: '#fbca6d', fontSize: 18, mt: 0.2 }} />
                      <Typography
                        variant="body2"
                        sx={{ color: '#fbca6d', fontStyle: 'italic', flex: 1 }}
                      >
                        &ldquo;{inspirationalQuote.quote_text}&rdquo;
                        {inspirationalQuote.author && (
                          <Typography component="span" sx={{ color: 'rgba(255,255,255,0.6)', ml: 1 }}>
                            — {inspirationalQuote.author}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  )}

                  {/* Two-column layout: Radar + Global Score */}
                  <Box 
                    display="grid" 
                    gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} 
                    gap={4} 
                    alignItems="start"
                  >
                    {/* LEFT COLUMN: Radar Chart */}
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="center"
                        sx={{
                          maxWidth: { xs: 350, sm: 450, md: 400 },
                          mx: 'auto',
                        }}
                      >
                        <Radar data={chartData} options={chartOptions} />
                      </Box>
                    </Box>

                    {/* RIGHT COLUMN: Global Balance Score & Quick Action */}
                    <Box>
                      {/* Global Balance Score Display */}
                      <Box mb={4}>
                        <Typography variant="h6" fontWeight={600} mb={2} sx={{ color: 'white' }}>
                          {/* LAP-733 (GE022) */}
                          {t('lifeReport.globalBalance')}
                        </Typography>
                        
                        {latestSnapshot && Number.isFinite(Number(latestSnapshot.global_score)) ? (
                          <>
                            <Box
                              sx={{
                                textAlign: 'center',
                                p: 3,
                                bgcolor: 'rgba(251, 202, 109, 0.08)',
                                borderRadius: 3,
                                border: '2px solid rgba(251, 202, 109, 0.3)',
                                mb: 3,
                              }}
                            >
                              <Typography 
                                variant="h2" 
                                fontWeight={700} 
                                sx={{ 
                                  color: '#fbca6d',
                                  fontSize: { xs: '2.5rem', md: '3rem' },
                                  lineHeight: 1,
                                  mb: 0.5,
                                }}
                              >
                                {Number(latestSnapshot.global_score).toFixed(1)}
                                <Typography 
                                  component="span" 
                                  variant="h4" 
                                  sx={{ 
                                    color: 'rgba(255,255,255,0.6)',
                                    ml: 1,
                                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                                  }}
                                >
                                  /10
                                </Typography>
                              </Typography>
                              
                              {/* Optional metadata */}
                              <Box 
                                display="flex" 
                                gap={2} 
                                justifyContent="center" 
                                flexWrap="wrap"
                                mt={2}
                                sx={{ color: 'rgba(255,255,255,0.6)' }}
                              >
                                {latestSnapshot.eligible_domain_count && (
                                  <Typography variant="caption" fontSize={11}>
                                    {/* LAP-733 (GE022) */}
                                    {t('lifeReport.domainsEvaluated', { count: latestSnapshot.eligible_domain_count })}
                                  </Typography>
                                )}
                                {Number.isFinite(Number(latestSnapshot.data_completeness_rate)) && (
                                  <Typography variant="caption" fontSize={11}>
                                    {/* LAP-733 (GE022) */}
                                    {t('lifeReport.completeness', { percent: Number(latestSnapshot.data_completeness_rate).toFixed(0) })}
                                  </Typography>
                                )}
                              </Box>
                            </Box>

                            {/* Global explanatory text */}
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'rgba(255,255,255,0.85)', 
                                lineHeight: 1.6,
                                textAlign: 'center',
                                mb: 2,
                              }}
                            >
                              {/* LAP-733 (GE022) — adjective is localized then
                                  interpolated into the phrase for proper grammar. */}
                              {t('lifeReport.balancePhrase', {
                                level:
                                  Number(latestSnapshot.global_score) >= 7
                                    ? t('lifeReport.balanceSolid')
                                    : Number(latestSnapshot.global_score) >= 5
                                    ? t('lifeReport.balanceModerate')
                                    : t('lifeReport.balanceImprove'),
                              })}
                            </Typography>

                            {/* Snapshot type indicator */}
                            <Box display="flex" justifyContent="center">
                              <Chip
                                /* LAP-733 (GE022) — i18n table preferred. */
                                label={I18N_SNAPSHOT_SOURCE_LABELS[latestSnapshot.snapshot_type] || latestSnapshot.snapshot_type}
                                size="small"
                                icon={latestSnapshot.snapshot_type === 'AUTO' ? <AutoAwesomeIcon /> : <EditIcon />}
                                sx={{
                                  bgcolor: SNAPSHOT_SOURCE_COLORS[latestSnapshot.snapshot_type] || '#666',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: 10,
                                  height: 22,
                                }}
                              />
                            </Box>
                          </>
                        ) : (
                          /* No data state for Global Balance Score */
                          <Box
                            sx={{
                              textAlign: 'center',
                              p: 3,
                              bgcolor: 'rgba(255, 255, 255, 0.03)',
                              borderRadius: 3,
                              border: '2px solid rgba(255, 255, 255, 0.1)',
                              mb: 3,
                            }}
                          >
                            <Typography 
                              variant="h2" 
                              fontWeight={700} 
                              sx={{ 
                                color: 'rgba(255,255,255,0.4)',
                                fontSize: { xs: '2.5rem', md: '3rem' },
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              —
                              <Typography 
                                component="span" 
                                variant="h4" 
                                sx={{ 
                                  color: 'rgba(255,255,255,0.3)',
                                  ml: 1,
                                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                                }}
                              >
                                /10
                              </Typography>
                            </Typography>
                            
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: 'rgba(255,255,255,0.6)', 
                                lineHeight: 1.6,
                                textAlign: 'center',
                                mt: 2,
                              }}
                            >
                              {/* LAP-733 (GE022) */}
                              {t('lifeReport.noDataPeriod')}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Quick Action - "Créer une idée" */}
                      <Box>
                        <Typography variant="h6" fontWeight={600} mb={2} sx={{ color: 'white' }}>
                          {/* LAP-733 (GE022) */}
                          {t('lifeReport.quickAction')}
                        </Typography>
                        
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={handleCreateIdea}
                          sx={{
                            width: '100%',
                            py: 1.5,
                            borderColor: '#fbca6d',
                            color: '#fbca6d',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderRadius: 2,
                            '&:hover': { 
                              borderColor: '#fbca6d', 
                              bgcolor: 'rgba(251, 202, 109, 0.08)',
                            },
                          }}
                        >
                          {/* LAP-733 (GE022) */}
                          {t('lifeReport.createIdea')}
                        </Button>
                        
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'rgba(255,255,255,0.6)', 
                            display: 'block',
                            textAlign: 'center',
                            mt: 1,
                            fontSize: 11,
                          }}
                        >
                          {/* LAP-733 (GE022) */}
                          {t('lifeReport.ideaHint')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Enhanced domain score cards with text templates */}
                {latestSnapshot?.domain_scores && (
                  <Box mt={5}>
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <Typography variant="h6" fontWeight={600}>
                        {/* LAP-733 (GE022) */}
                        {t('lifeReport.domainAnalysisTitle')}
                      </Typography>
                      <Tooltip title={t('lifeReport.help')}>
                        <IconButton
                          onClick={() => setShowDomainHelpDialog(true)}
                          sx={{
                            color: 'rgba(255,255,255,0.6)',
                            fontSize: 16,
                            p: 0.5,
                            '&:hover': {
                              color: 'rgba(255,255,255,0.8)',
                            },
                          }}
                        >
                          <InfoOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <Box
                      display="grid"
                      gridTemplateColumns={{
                        xs: '1fr',
                        sm: '1fr 1fr',
                        md: 'repeat(4, 1fr)',
                      }}
                      gap={2}
                    >
                      {/* Always display the 8 domains in stable order (same as radar) */}
                      {['Finance', 'Temps', 'Earth', 'Social', 'Professionnel', 'Personnel', 'Domicile', 'Administratif'].map((domainKey) => {
                        const ds = latestSnapshot.domain_scores.find(score => score.domain === domainKey)
                        const isEligible = ds?.is_eligible !== false
                        const score = ds?.score || ds?.final_score
                        // « En attente de données » : le domaine a une note réelle
                        // (souvent 0) mais faute de données récentes — ce n'est pas
                        // une contre-performance, on invite à compléter (pas un report).
                        const isAwaitingData =
                          isEligible &&
                          ds?.computed_from_trace === false &&
                          ds?.ineligibility_reason === 'NO_DATA'
                        // « Estimation IA » : sur la note du jour (AUTO), le domaine
                        // n'a pas d'objectif défini mais des données réelles — un
                        // « maître de domaine » IA a estimé la note (pas la formule,
                        // et ce n'est pas un état "en attente").
                        const isAiEstimated =
                          isEligible &&
                          latestSnapshot?.snapshot_type === 'AUTO' &&
                          ds?.computed_from_trace === false &&
                          !ds?.ineligibility_reason &&
                          score !== null && score !== undefined
                        const domainImage = ds?.domain_metadata?.image
                        const frozenText = ds?.domain_metadata?.frozen_text
                        
                        return (
                          <Card
                            key={domainKey}
                            onClick={isEligible ? () => navigate(`/life-resume/domaine/${domainKey}`) : undefined}
                            sx={{
                              position: 'relative',
                              minHeight: 200,
                              bgcolor: 'rgba(255,255,255,0.05)',
                              borderRadius: 2,
                              border: '1px solid rgba(255,255,255,0.08)',
                              overflow: 'hidden',
                              transition: 'all 0.2s',
                              cursor: isEligible ? 'pointer' : 'default',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.08)',
                                transform: isEligible ? 'translateY(-2px)' : 'none',
                              },
                              opacity: isEligible ? 1 : 0.6,
                            }}
                          >
                            {/* Background image with overlay */}
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundImage: domainImage 
                                  ? `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8)), url('${domainImage}')`
                                  : 'linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8))',
                                backgroundColor: domainImage ? 'transparent' : 'rgba(40, 56, 77, 0.8)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                              }}
                            />
                            
                            <CardContent sx={{ position: 'relative', zIndex: 1, p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                              {/* Header zone: domain + score */}
                              <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      color: isEligible ? 'white' : 'rgba(255,255,255,0.5)',
                                      fontWeight: 700,
                                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                                      fontSize: 16,
                                    }}
                                  >
                                    {DOMAIN_LABELS[domainKey] || domainKey}
                                  </Typography>
                                  {isAwaitingData ? (
                                    <Chip
                                      label={t('lifeReport.awaitingData', 'En attente de données')}
                                      size="small"
                                      sx={{
                                        alignSelf: 'flex-start',
                                        bgcolor: 'rgba(251, 202, 109, 0.85)',
                                        color: '#0f1c2e',
                                        fontWeight: 700,
                                        fontSize: 9,
                                        height: 16,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  ) : isAiEstimated ? (
                                    <Chip
                                      label={t('lifeReport.aiEstimated', 'Estimation IA')}
                                      size="small"
                                      sx={{
                                        alignSelf: 'flex-start',
                                        bgcolor: 'rgba(129, 140, 248, 0.85)',
                                        color: '#0f1c2e',
                                        fontWeight: 700,
                                        fontSize: 9,
                                        height: 16,
                                        '& .MuiChip-label': { px: 0.75 },
                                      }}
                                    />
                                  ) : (
                                    isEligible && score !== null && score !== undefined && latestSnapshot?.snapshot_type && (
                                      <Chip
                                        /* LAP-733 (GE022) — i18n table preferred. */
                                        label={I18N_SNAPSHOT_SOURCE_LABELS[latestSnapshot.snapshot_type] || latestSnapshot.snapshot_type}
                                        size="small"
                                        sx={{
                                          alignSelf: 'flex-start',
                                          bgcolor: SNAPSHOT_SOURCE_COLORS[latestSnapshot.snapshot_type] || '#666',
                                          color: 'white',
                                          fontWeight: 600,
                                          fontSize: 9,
                                          height: 16,
                                          '& .MuiChip-label': { px: 0.75 },
                                        }}
                                      />
                                    )
                                  )}
                                </Box>
                                
                                {/* Domain score */}
                                <Box textAlign="right">
                                  {isEligible && score !== null && score !== undefined ? (
                                    <Typography
                                      variant="h4"
                                      sx={{
                                        color: isAwaitingData ? 'rgba(255,255,255,0.55)' : '#fbca6d',
                                        fontWeight: 700,
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                                        fontSize: 28,
                                        lineHeight: 1,
                                      }}
                                    >
                                      {Number(score).toLocaleString('fr-FR', {
                                        minimumFractionDigits: 1,
                                        maximumFractionDigits: 1
                                      })}
                                    </Typography>
                                  ) : (
                                    <Typography 
                                      variant="h4" 
                                      sx={{ 
                                        color: 'rgba(255,255,255,0.3)',
                                        fontWeight: 700,
                                        fontSize: 28,
                                        lineHeight: 1,
                                      }}
                                    >
                                      —
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* Disabled state overlay lock icon */}
                              {!isEligible && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                  }}
                                >
                                  <LockIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                                </Box>
                              )}

                              {/* Text zone: frozen explanatory text */}
                              <Box mt="auto">
                                {isAwaitingData ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'rgba(251, 202, 109, 0.95)',
                                      lineHeight: 1.4,
                                      fontSize: 13,
                                      fontWeight: 600,
                                      textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                                    }}
                                  >
                                    {t(
                                      'lifeReport.awaitingDataHint',
                                      'Complétez vos données récentes pour calculer cette note. Cliquez pour voir ce qu\'il manque.',
                                    )}
                                  </Typography>
                                ) : isEligible ? (
                                  frozenText?.short_text ? (
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'rgba(255,255,255,0.9)',
                                        lineHeight: 1.4,
                                        fontSize: 13,
                                        textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                                      }}
                                    >
                                      {frozenText.short_text}
                                    </Typography>
                                  ) : (
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        color: 'rgba(255,255,255,0.6)',
                                        lineHeight: 1.4,
                                        fontSize: 13,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      {/* LAP-733 (GE022) */}
                                      {t('lifeReport.analysisLoading')}
                                    </Typography>
                                  )
                                ) : (
                                  <Box>
                                    {/* State 2: Plan Gated */}
                                    {ds?.ineligibility_reason === 'PLAN_GATED' ? (
                                      <Box>
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            color: 'rgba(255,255,255,0.4)',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            display: 'block',
                                            mb: 0.5,
                                          }}
                                        >
                                          {/* LAP-733 (GE022) */}
                                          {ds?.domain_metadata?.required_plan === 'PREMIUM'
                                            ? t('lifeReport.availableWithPremium')
                                            : ds?.domain_metadata?.required_plan === 'STANDARD'
                                            ? t('lifeReport.availableWithStandard')
                                            : t('lifeReport.subscriptionRequired')}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      /* State 3: No Data */
                                      <Box>
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            color: 'rgba(255,255,255,0.4)',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            display: 'block',
                                            mb: 0.5,
                                          }}
                                        >
                                          {/* LAP-733 (GE022) */}
                                          {t('lifeReport.missingData')}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'rgba(255,255,255,0.3)',
                                            fontSize: 10,
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {/* LAP-733 (GE022) */}
                                          {t('lifeReport.missingDataHint')}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        )
                      })}
                    </Box>
                  </Box>
                )}

                {/* Action buttons */}
                <Box display="flex" gap={2} justifyContent="center" mt={4} flexWrap="wrap">
                  <Button
                    size="small"
                    onClick={handleRegenerateAuto}
                    disabled={regenerating}
                    startIcon={regenerating ? <CircularProgress size={14} /> : <AutoAwesomeIcon />}
                    sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: 13 }}
                  >
                    {/* LAP-733 (GE022) */}
                    {regenerating ? t('lifeReport.regenerating') : t('lifeReport.regenAuto')}
                  </Button>
                  <Button
                    size="small"
                    onClick={handleCreateManual}
                    startIcon={<EditIcon />}
                    sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontSize: 13 }}
                  >
                    {/* LAP-733 (GE022) */}
                    {t('lifeReport.newManualEval')}
                  </Button>
                </Box>
              </>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* Info Dialog */}
      <Dialog
        open={showInfoDialog}
        onClose={() => setShowInfoDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.appBar?.main || '#0a1929',
            color: 'white',
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <Box p={3}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <InfoOutlinedIcon sx={{ color: '#fbca6d', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              {/* LAP-733 (GE022) */}
              {t('lifeReport.about')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7 }}>
            {/* LAP-733 (GE022) */}
            {t('lifeReport.aboutP1')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, mt: 2 }}>
            {/* LAP-733 (GE022) */}
            {t('lifeReport.aboutP2')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, mt: 2 }}>
            {/* LAP-733 (GE022) */}
            {t('lifeReport.aboutP3')}
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              onClick={() => setShowInfoDialog(false)}
              sx={{
                color: '#fbca6d',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {/* LAP-733 (GE022) */}
              {t('lifeReport.understood')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Period Selector Dialog */}
      <PeriodSelectorDialog
        open={showPeriodDialog}
        onClose={() => setShowPeriodDialog(false)}
        availablePeriods={availablePeriods}
        currentPeriod={selectedPeriod}
        onApply={handlePeriodApply}
      />

      {/* Domain Help Dialog */}
      <Dialog
        open={showDomainHelpDialog}
        onClose={() => setShowDomainHelpDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.appBar?.main || '#0a1929',
            color: 'white',
            borderRadius: 3,
            p: 1,
          },
        }}
      >
        <Box p={3}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2}>
            <InfoOutlinedIcon sx={{ color: '#fbca6d', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              {/* LAP-733 (GE022) */}
              {t('lifeReport.domainAnalysisTitle')}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7 }}>
            {/* LAP-733 (GE022) */}
            {t('lifeReport.domainHelpP1')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.7, mt: 2 }}>
            {/* LAP-733 (GE022) */}
            {t('lifeReport.domainHelpP2')}
          </Typography>
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button
              onClick={() => setShowDomainHelpDialog(false)}
              sx={{
                color: '#fbca6d',
                textTransform: 'none',
                fontWeight: 600,
              }}
            >
              {/* LAP-733 (GE022) */}
              {t('lifeReport.understood')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Manual Evaluation Dialog */}
      <Dialog
        open={showManualDialog}
        onClose={() => setShowManualDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.appBar?.main || '#0a1929',
            color: 'white',
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        <Box p={3}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            <EditIcon sx={{ color: '#fbca6d', fontSize: 28 }} />
            <Typography variant="h6" fontWeight={600}>
              {/* LAP-733 (GE022) */}
              {t('lifeReport.manualEval')}
              {selectedPeriod && (
                <Typography component="span" sx={{ color: 'rgba(255,255,255,0.7)', ml: 1, fontSize: '0.9rem', fontWeight: 400 }}>
                  — {formatPeriodLabel(selectedPeriod)}
                </Typography>
              )}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
            {/* LAP-733 (GE022) */}
            {t('lifeReport.evalScale')}
            {selectedPeriod && (
              <Typography component="span" sx={{ display: 'block', mt: 1, color: '#fbca6d', fontSize: '0.85rem' }}>
                {t('lifeReport.period')} : {formatPeriodLabel(selectedPeriod)}
              </Typography>
            )}
          </Typography>

          <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
            {allDomains.map((domain) => {
              const isLocked = domain.required_plan && domain.required_plan !== 'FREE'
              const scoreValue = manualScores[domain.domain]
              const currentScore = (scoreValue !== undefined && scoreValue !== null && !isNaN(Number(scoreValue))) 
                ? Number(scoreValue) 
                : 5

              return (
                <Box
                  key={domain.domain}
                  sx={{
                    mb: 3,
                    p: 2.5,
                    bgcolor: 'rgba(255,255,255,0.03)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.08)',
                    opacity: isLocked ? 0.7 : 1,
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {DOMAIN_LABELS[domain.domain] || domain.domain}
                      </Typography>
                      {isLocked && (
                        <Chip
                          icon={<LockIcon sx={{ fontSize: 12 }} />}
                          /* LAP-733 (GE022) — i18n plan label */
                          label={I18N_PLAN_LABELS[domain.required_plan] || PLAN_LABELS[domain.required_plan]}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            bgcolor: PLAN_COLORS[domain.required_plan],
                            color: 'white',
                          }}
                        />
                      )}
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ color: '#fbca6d' }}>
                      {currentScore.toFixed(1)}
                    </Typography>
                  </Box>
                  <Slider
                    value={currentScore}
                    onChange={(e, value) => handleManualScoreChange(domain.domain, value)}
                    min={0}
                    max={10}
                    step={0.5}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                    ]}
                    sx={{
                      color: '#fbca6d',
                      '& .MuiSlider-markLabel': {
                        color: 'rgba(255,255,255,0.5)',
                        fontSize: 11,
                      },
                      '& .MuiSlider-mark': {
                        bgcolor: 'rgba(255,255,255,0.3)',
                      },
                    }}
                  />
                </Box>
              )
            })}
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3} pt={2} borderTop="1px solid rgba(255,255,255,0.08)">
            <Button
              onClick={() => setShowManualDialog(false)}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'none',
              }}
            >
              {/* LAP-733 (GE022) */}
              {t('lifeReport.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveManualEvaluation}
              sx={{
                bgcolor: '#fbca6d',
                color: '#0a1929',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#f5c05d',
                },
              }}
            >
              {/* LAP-733 (GE022) */}
              {t('lifeReport.saveEval')}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Add Idea Dialog */}
      <AddIdeaPopup
        open={showAddIdeaDialog}
        onClose={() => setShowAddIdeaDialog(false)}
        onSubmit={handleIdeaSubmit}
      />
    </Box>
  )
}